import * as React from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { AgentRole } from '../../models/Support/SupportEnums';
import { emptyOnForbidden, supportQueryRetry } from '../../utils/Support/supportHelpers';
import { SUPPORT_PATHS, SUPPORT_QUERY_KEYS } from './supportQueryHelpers';

interface UseAgentConfigMutationOptions {
	onSuccess?: () => void;
	onError?: (error: unknown) => void;
}

interface UpdateAgentConfigMutationInput {
	id: string;
	isAvailable: boolean;
	maxTickets: number | null;
	role: AgentRole;
}

export interface ISupportAgentConfig {
	id: string;
	mailboxId: string;
	agentId: string;
	agentName?: string;
	agentEmail?: string;
	agentProfilePic?: string;
	isAvailable: boolean;
	maxTickets?: number | null;
	role?: AgentRole;
}

/** Resolved server-side, so defaults are already applied and no fallback logic belongs here. */
export interface ISupportNotificationSettings {
	emailOnAssigned: boolean;
	emailOnMentioned: boolean;
	emailOnTicketActivity: boolean;
	inAppOnAssigned: boolean;
	inAppOnMentioned: boolean;
	inAppOnTicketActivity: boolean;
}

export interface IMyAgentConfig {
	role: AgentRole | 'none';
	isAgent: boolean;
	isAvailable: boolean;
	notifications?: ISupportNotificationSettings;
}

/** Partial: only the fields sent are written, so omitting the in-app ones cannot clear them. */
export type SupportNotificationSettingsUpdate = Partial<ISupportNotificationSettings>;

export function useMyAgentConfigQuery(mailboxId: string) {
	const userSession = useUserSession();
	return useQuery({
		queryKey: SUPPORT_QUERY_KEYS.mailboxes.myAgentConfig(mailboxId),
		staleTime: 30_000,
		queryFn: () =>
			userSession.webServiceHelper.callAsync<IMyAgentConfig>(SUPPORT_PATHS.mailboxes.myAgent(mailboxId), 'GET'),
		enabled: !!mailboxId,
		meta: { toastErrorMessage: 'Failed to load your agent settings' },
	});
}

/** One query per inbox — the endpoint is per-mailbox, and an agent is realistically on one or two. */
export function useMyAgentConfigsQuery(mailboxIds: string[]) {
	const userSession = useUserSession();
	return useQueries({
		queries: mailboxIds.map(mailboxId => ({
			queryKey: SUPPORT_QUERY_KEYS.mailboxes.myAgentConfig(mailboxId),
			staleTime: 30_000,
			queryFn: () =>
				userSession.webServiceHelper.callAsync<IMyAgentConfig>(SUPPORT_PATHS.mailboxes.myAgent(mailboxId), 'GET'),
		})),
		combine: results => ({
			isLoading: results.some(r => r.isLoading),
			isError: results.some(r => r.isError),
			configs: results.map((r, i) => ({ mailboxId: mailboxIds[i], data: r.data })),
		}),
	});
}

export function useUpdateMyNotificationPreferencesMutation({ onSuccess, onError }: UseAgentConfigMutationOptions = {}) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ mailboxId, settings }: { mailboxId: string; settings: SupportNotificationSettingsUpdate }) =>
			userSession.webServiceHelper.callAsync<IMyAgentConfig>(
				SUPPORT_PATHS.mailboxes.myAgent(mailboxId),
				'PUT',
				settings
			),
		onSuccess: (updated, { mailboxId }) => {
			// Seed from the response rather than refetching: it already carries the resolved settings.
			queryClient.setQueryData(SUPPORT_QUERY_KEYS.mailboxes.myAgentConfig(mailboxId), updated);
			onSuccess?.();
		},
		onError,
	});
}

interface ISupportMailboxUserAccess {
	userId: string;
	canAccess: boolean;
}

const MAILBOX_USER_ACCESS_GRANT_STALE_MS = 60_000;

/**
 * Asks the server whether a user can open a mailbox's tickets. An imperative fetch, not a hook-bound
 * query: the mention picker asks only for the one user picked. Only a grant is reused; a denial is asked
 * again, since the usual next step is an admin granting access and the author trying once more.
 */
export function useFetchMailboxUserAccess(mailboxId: string) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	return React.useCallback(
		(userId: string) =>
			queryClient.fetchQuery({
				queryKey: SUPPORT_QUERY_KEYS.mailboxes.userAccess(mailboxId, userId),
				staleTime: query => (query.state.data?.canAccess ? MAILBOX_USER_ACCESS_GRANT_STALE_MS : 0),
				queryFn: () =>
					userSession.webServiceHelper.callAsync<ISupportMailboxUserAccess>(
						SUPPORT_PATHS.mailboxes.userAccess(mailboxId, userId),
						'GET'
					),
			}),
		[mailboxId, queryClient, userSession]
	);
}

export function useAgentConfigsQuery(mailboxId: string) {
	const userSession = useUserSession();
	return useQuery({
		queryKey: SUPPORT_QUERY_KEYS.mailboxes.agentConfigs(mailboxId),
		staleTime: 30_000,
		retry: supportQueryRetry,
		queryFn: () =>
			emptyOnForbidden(
				() =>
					userSession.webServiceHelper.callAsync<{ values: ISupportAgentConfig[] }>(
						SUPPORT_PATHS.mailboxes.agents(mailboxId),
						'GET'
					),
				{ values: [] }
			),
		enabled: !!mailboxId,
		meta: { toastErrorMessage: 'Failed to load support team' },
	});
}

export function useAddAgentConfigMutation(
	mailboxId: string,
	{ onSuccess, onError }: UseAgentConfigMutationOptions = {}
) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (agent: { agentId: string; agentName: string }) =>
			userSession.webServiceHelper.callAsync(SUPPORT_PATHS.mailboxes.agents(mailboxId), 'POST', agent),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.mailboxes.agentConfigs(mailboxId) });
			queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.mailboxes.myAgentConfig(mailboxId) });
			onSuccess?.();
		},
		onError,
	});
}

export function useUpdateAgentConfigMutation(
	mailboxId: string,
	{ onSuccess, onError }: UseAgentConfigMutationOptions = {}
) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, ...data }: UpdateAgentConfigMutationInput) =>
			userSession.webServiceHelper.callAsync(SUPPORT_PATHS.mailboxes.agent(mailboxId, id), 'PUT', data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.mailboxes.agentConfigs(mailboxId) });
			// An agent updating their own row (e.g. toggling availability) must also refresh
			// the current user's `me` config, which other surfaces read for "am I available".
			queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.mailboxes.myAgentConfig(mailboxId) });
			onSuccess?.();
		},
		onError,
	});
}

export function useDeleteAgentConfigMutation(
	mailboxId: string,
	{ onSuccess, onError }: UseAgentConfigMutationOptions = {}
) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: string) =>
			userSession.webServiceHelper.callAsync(SUPPORT_PATHS.mailboxes.agent(mailboxId, id), 'DELETE'),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.mailboxes.agentConfigs(mailboxId) });
			queryClient.invalidateQueries({ queryKey: SUPPORT_QUERY_KEYS.mailboxes.myAgentConfig(mailboxId) });
			onSuccess?.();
		},
		onError,
	});
}
